import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Bell, Download, Brain, TrendingUp, TrendingDown, DollarSign,
  ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown,
  BarChart3, PieChart, Eye, Activity, Layers, Percent,
  ArrowRightLeft, Flame, Zap
} from 'lucide-react';
import './ETFHoldingsPage.css';

// =============================================================================
// MOCK DATA — ETF holdings, flows, and performance
// =============================================================================

const mockETFs = [
  { id: 1, ticker: 'SPY', name: 'SPDR S&P 500 ETF Trust', issuer: 'State Street', category: 'Large Cap Blend', aum: 562000000000, expenseRatio: 0.0945, price: 596.42, ytdReturn: 4.2, flow30d: 8200000000, holdings: 503, topHolding: 'AAPL', topWeight: 7.1, inception: '1993-01-22' },
  { id: 2, ticker: 'VOO', name: 'Vanguard S&P 500 ETF', issuer: 'Vanguard', category: 'Large Cap Blend', aum: 498000000000, expenseRatio: 0.03, price: 548.18, ytdReturn: 4.3, flow30d: 6100000000, holdings: 503, topHolding: 'AAPL', topWeight: 7.1, inception: '2010-09-07' },
  { id: 3, ticker: 'QQQ', name: 'Invesco QQQ Trust', issuer: 'Invesco', category: 'Large Cap Growth', aum: 312000000000, expenseRatio: 0.20, price: 528.74, ytdReturn: 5.8, flow30d: 4800000000, holdings: 101, topHolding: 'AAPL', topWeight: 8.9, inception: '1999-03-10' },
  { id: 4, ticker: 'IWM', name: 'iShares Russell 2000 ETF', issuer: 'BlackRock', category: 'Small Cap Blend', aum: 78000000000, expenseRatio: 0.19, price: 224.56, ytdReturn: 1.2, flow30d: -1200000000, holdings: 1975, topHolding: 'SMCI', topWeight: 0.6, inception: '2000-05-22' },
  { id: 5, ticker: 'VTI', name: 'Vanguard Total Stock Market ETF', issuer: 'Vanguard', category: 'Total Market', aum: 425000000000, expenseRatio: 0.03, price: 292.80, ytdReturn: 3.9, flow30d: 5400000000, holdings: 3645, topHolding: 'AAPL', topWeight: 6.4, inception: '2001-05-24' },
  { id: 6, ticker: 'ARKK', name: 'ARK Innovation ETF', issuer: 'ARK Invest', category: 'Thematic Growth', aum: 6800000000, expenseRatio: 0.75, price: 58.92, ytdReturn: 12.4, flow30d: -340000000, holdings: 35, topHolding: 'TSLA', topWeight: 11.2, inception: '2014-10-31' },
  { id: 7, ticker: 'XLF', name: 'Financial Select Sector SPDR', issuer: 'State Street', category: 'Financials', aum: 42000000000, expenseRatio: 0.09, price: 48.12, ytdReturn: 6.1, flow30d: 1800000000, holdings: 73, topHolding: 'BRK.B', topWeight: 13.4, inception: '1998-12-16' },
  { id: 8, ticker: 'XLE', name: 'Energy Select Sector SPDR', issuer: 'State Street', category: 'Energy', aum: 35000000000, expenseRatio: 0.09, price: 86.24, ytdReturn: -2.8, flow30d: -920000000, holdings: 23, topHolding: 'XOM', topWeight: 22.8, inception: '1998-12-16' },
  { id: 9, ticker: 'SCHD', name: 'Schwab U.S. Dividend Equity ETF', issuer: 'Schwab', category: 'Dividend', aum: 62000000000, expenseRatio: 0.06, price: 28.94, ytdReturn: 2.1, flow30d: 2100000000, holdings: 103, topHolding: 'ABBV', topWeight: 4.2, inception: '2011-10-20' },
  { id: 10, ticker: 'GLD', name: 'SPDR Gold Shares', issuer: 'State Street', category: 'Commodities', aum: 72000000000, expenseRatio: 0.40, price: 248.60, ytdReturn: 8.9, flow30d: 3200000000, holdings: 1, topHolding: 'Gold Bullion', topWeight: 100, inception: '2004-11-18' },
  { id: 11, ticker: 'TLT', name: 'iShares 20+ Year Treasury Bond', issuer: 'BlackRock', category: 'Long-Term Bond', aum: 54000000000, expenseRatio: 0.15, price: 88.42, ytdReturn: -3.4, flow30d: -1600000000, holdings: 34, topHolding: 'UST 30Y', topWeight: 8.2, inception: '2002-07-22' },
  { id: 12, ticker: 'SOXX', name: 'iShares Semiconductor ETF', issuer: 'BlackRock', category: 'Semiconductors', aum: 14800000000, expenseRatio: 0.35, price: 248.16, ytdReturn: 7.6, flow30d: 890000000, holdings: 30, topHolding: 'NVDA', topWeight: 10.4, inception: '2001-07-10' },
  { id: 13, ticker: 'VWO', name: 'Vanguard FTSE Emerging Markets', issuer: 'Vanguard', category: 'Emerging Markets', aum: 82000000000, expenseRatio: 0.08, price: 44.28, ytdReturn: 2.6, flow30d: 1400000000, holdings: 5824, topHolding: 'TSM', topWeight: 5.8, inception: '2005-03-04' },
  { id: 14, ticker: 'XLK', name: 'Technology Select Sector SPDR', issuer: 'State Street', category: 'Technology', aum: 68000000000, expenseRatio: 0.09, price: 232.48, ytdReturn: 5.2, flow30d: 2400000000, holdings: 68, topHolding: 'AAPL', topWeight: 14.8, inception: '1998-12-16' },
  { id: 15, ticker: 'VNQ', name: 'Vanguard Real Estate ETF', issuer: 'Vanguard', category: 'Real Estate', aum: 34000000000, expenseRatio: 0.12, price: 86.14, ytdReturn: -1.2, flow30d: -450000000, holdings: 160, topHolding: 'PLD', topWeight: 7.6, inception: '2004-09-23' },
  { id: 16, ticker: 'IBIT', name: 'iShares Bitcoin Trust ETF', issuer: 'BlackRock', category: 'Crypto', aum: 52000000000, expenseRatio: 0.25, price: 56.82, ytdReturn: 14.8, flow30d: 4200000000, holdings: 1, topHolding: 'Bitcoin', topWeight: 100, inception: '2024-01-11' },
  { id: 17, ticker: 'XLV', name: 'Health Care Select Sector SPDR', issuer: 'State Street', category: 'Healthcare', aum: 38000000000, expenseRatio: 0.09, price: 142.68, ytdReturn: 1.8, flow30d: 680000000, holdings: 62, topHolding: 'LLY', topWeight: 11.2, inception: '1998-12-16' },
  { id: 18, ticker: 'BND', name: 'Vanguard Total Bond Market ETF', issuer: 'Vanguard', category: 'Total Bond', aum: 114000000000, expenseRatio: 0.03, price: 72.56, ytdReturn: 0.4, flow30d: 1200000000, holdings: 10702, topHolding: 'UST Various', topWeight: 42.8, inception: '2007-04-03' },
  { id: 19, ticker: 'DIA', name: 'SPDR Dow Jones Industrial Avg', issuer: 'State Street', category: 'Large Cap Value', aum: 36000000000, expenseRatio: 0.16, price: 442.38, ytdReturn: 3.1, flow30d: 920000000, holdings: 30, topHolding: 'UNH', topWeight: 8.4, inception: '1998-01-14' },
  { id: 20, ticker: 'KWEB', name: 'KraneShares CSI China Internet', issuer: 'KraneShares', category: 'China Tech', aum: 6200000000, expenseRatio: 0.69, price: 32.84, ytdReturn: 18.2, flow30d: 820000000, holdings: 40, topHolding: 'PDD', topWeight: 9.8, inception: '2013-07-31' },
];

const mockTopInflows = [
  { ticker: 'SPY', name: 'S&P 500 ETF', flow: 8200000000, pctAUM: 1.5 },
  { ticker: 'VOO', name: 'Vanguard S&P 500', flow: 6100000000, pctAUM: 1.2 },
  { ticker: 'VTI', name: 'Total Stock Market', flow: 5400000000, pctAUM: 1.3 },
  { ticker: 'QQQ', name: 'Nasdaq 100', flow: 4800000000, pctAUM: 1.5 },
  { ticker: 'IBIT', name: 'Bitcoin Trust', flow: 4200000000, pctAUM: 8.1 },
];

const mockTopOutflows = [
  { ticker: 'TLT', name: '20+ Year Treasury', flow: -1600000000, pctAUM: -3.0 },
  { ticker: 'IWM', name: 'Russell 2000', flow: -1200000000, pctAUM: -1.5 },
  { ticker: 'XLE', name: 'Energy Sector', flow: -920000000, pctAUM: -2.6 },
  { ticker: 'VNQ', name: 'Real Estate', flow: -450000000, pctAUM: -1.3 },
  { ticker: 'ARKK', name: 'ARK Innovation', flow: -340000000, pctAUM: -5.0 },
];

const mockCategoryPerformance = [
  { name: 'China Tech', ytd: 18.2, color: '#ef4444' },
  { name: 'Crypto', ytd: 14.8, color: '#f59e0b' },
  { name: 'Thematic Growth', ytd: 12.4, color: '#8b5cf6' },
  { name: 'Commodities', ytd: 8.9, color: '#10b981' },
  { name: 'Semiconductors', ytd: 7.6, color: '#3b82f6' },
  { name: 'Financials', ytd: 6.1, color: '#667eea' },
  { name: 'Large Cap Growth', ytd: 5.8, color: '#818cf8' },
  { name: 'Large Cap Blend', ytd: 4.2, color: '#6b7280' },
  { name: 'Long-Term Bond', ytd: -3.4, color: '#ef4444' },
];

const mockLargestETFs = [
  { ticker: 'SPY', name: 'S&P 500 ETF', aum: 562000000000, expense: 0.0945 },
  { ticker: 'VOO', name: 'Vanguard S&P 500', aum: 498000000000, expense: 0.03 },
  { ticker: 'VTI', name: 'Total Stock Market', aum: 425000000000, expense: 0.03 },
  { ticker: 'QQQ', name: 'Nasdaq 100', aum: 312000000000, expense: 0.20 },
  { ticker: 'BND', name: 'Total Bond Market', aum: 114000000000, expense: 0.03 },
];

const mockHotETFs = [
  { ticker: 'IBIT', name: 'iShares Bitcoin Trust', aum: 52000000000, flow30d: 4200000000, launched: '2024', growth: '+8.1% AUM' },
  { ticker: 'KWEB', name: 'CSI China Internet', aum: 6200000000, flow30d: 820000000, launched: '2013', growth: '+18.2% YTD' },
  { ticker: 'SOXX', name: 'Semiconductor ETF', aum: 14800000000, flow30d: 890000000, launched: '2001', growth: '+7.6% YTD' },
  { ticker: 'ARKK', name: 'ARK Innovation', aum: 6800000000, flow30d: -340000000, launched: '2014', growth: '+12.4% YTD' },
];

// =============================================================================
// HELPERS
// =============================================================================

const formatCurrency = (num) => {
  if (!num && num !== 0) return '—';
  if (Math.abs(num) >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
  if (Math.abs(num) >= 1e9) return `$${(num / 1e9).toFixed(1)}B`;
  if (Math.abs(num) >= 1e6) return `$${(num / 1e6).toFixed(1)}M`;
  if (Math.abs(num) >= 1e3) return `$${(num / 1e3).toFixed(0)}K`;
  return `$${num.toFixed(0)}`;
};

const formatNumber = (num) => num?.toLocaleString() ?? '—';

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

function AIInsightCard({ insight }) {
  return (
    <div className="etf-ai-insight">
      <div className="etf-ai-header">
        <div className="etf-ai-badge"><Brain size={14} /> AI Insight</div>
        <span className="etf-ai-updated">Updated 1 hour ago</span>
      </div>
      <p className="etf-ai-text" dangerouslySetInnerHTML={{ __html: insight }} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, subType = 'neutral', color }) {
  return (
    <div className="etf-stat-card" style={{ background: color }}>
      <div className="etf-stat-icon"><Icon size={20} /></div>
      <div className="etf-stat-label">{label}</div>
      <div className="etf-stat-value">{value}</div>
      <div className={`etf-stat-sub ${subType}`}>{sub}</div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function ETFHoldingsPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [sortBy, setSortBy] = useState('aum');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 20;

  const categories = ['All', 'Equity', 'Fixed Income', 'Sector', 'Thematic', 'Commodity'];

  const getCategoryMatch = (cat, filter) => {
    if (filter === 'All') return true;
    if (filter === 'Equity') return ['Large Cap Blend', 'Large Cap Growth', 'Large Cap Value', 'Small Cap Blend', 'Total Market', 'Emerging Markets', 'China Tech'].includes(cat);
    if (filter === 'Fixed Income') return ['Long-Term Bond', 'Total Bond'].includes(cat);
    if (filter === 'Sector') return ['Financials', 'Energy', 'Technology', 'Healthcare', 'Real Estate', 'Semiconductors'].includes(cat);
    if (filter === 'Thematic') return ['Thematic Growth', 'Dividend', 'Crypto'].includes(cat);
    if (filter === 'Commodity') return ['Commodities'].includes(cat);
    return true;
  };

  const filteredETFs = useMemo(() => {
    let data = [...mockETFs];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      data = data.filter(e => e.ticker.toLowerCase().includes(q) || e.name.toLowerCase().includes(q) || e.issuer.toLowerCase().includes(q) || e.category.toLowerCase().includes(q));
    }
    if (categoryFilter !== 'All') {
      data = data.filter(e => getCategoryMatch(e.category, categoryFilter));
    }
    data.sort((a, b) => {
      const order = sortOrder === 'asc' ? 1 : -1;
      if (sortBy === 'ticker') return order * a.ticker.localeCompare(b.ticker);
      if (sortBy === 'aum') return order * (a.aum - b.aum);
      if (sortBy === 'ytdReturn') return order * (a.ytdReturn - b.ytdReturn);
      if (sortBy === 'flow30d') return order * (a.flow30d - b.flow30d);
      if (sortBy === 'expenseRatio') return order * (a.expenseRatio - b.expenseRatio);
      if (sortBy === 'holdings') return order * (a.holdings - b.holdings);
      return 0;
    });
    return data;
  }, [searchTerm, categoryFilter, sortBy, sortOrder]);

  const totalPages = Math.ceil(filteredETFs.length / perPage);
  const pagedETFs = filteredETFs.slice((currentPage - 1) * perPage, currentPage * perPage);

  const handleSort = (field) => {
    if (sortBy === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown size={12} style={{ opacity: 0.3 }} />;
    return sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
  };

  const stats = useMemo(() => {
    const totalAUM = mockETFs.reduce((s, e) => s + e.aum, 0);
    const totalFlow = mockETFs.reduce((s, e) => s + e.flow30d, 0);
    const avgReturn = (mockETFs.reduce((s, e) => s + e.ytdReturn, 0) / mockETFs.length).toFixed(1);
    const avgExpense = (mockETFs.reduce((s, e) => s + e.expenseRatio, 0) / mockETFs.length).toFixed(2);
    return { totalAUM, totalFlow, avgReturn, avgExpense, count: mockETFs.length };
  }, []);

  const aiInsight = `<span class="highlight">ETF flows signal risk-on rotation.</span> S&P 500 trackers (<span class="highlight">SPY</span>, <span class="highlight">VOO</span>) pulled in $14.3B combined over 30 days. <span class="highlight">IBIT</span> continues to dominate crypto flows with $4.2B in new capital (8.1% of AUM). Meanwhile, <span class="highlight">TLT</span> and <span class="highlight">XLE</span> seeing sustained outflows as investors move away from duration risk and energy. <span class="highlight">KWEB</span> is the YTD performance leader at +18.2%, riding the China tech recovery.`;

  return (
    <div className="etf-holdings-page">
      {/* Header */}
      <div className="etf-page-header">
        <div className="etf-header-content">
          <div className="etf-header-title">
            <div className="etf-header-icon"><Layers size={22} color="#fff" /></div>
            <div>
              <h1>ETF Holdings</h1>
              <p>Fund flows, holdings composition, and performance across major ETFs</p>
            </div>
          </div>
          <div className="etf-header-actions">
            <button className="etf-header-btn"><Download size={14} /> Export</button>
            <button className="etf-header-btn primary"><Bell size={14} /> Set Alerts</button>
          </div>
        </div>
      </div>

      {/* AI Insight */}
      <AIInsightCard insight={aiInsight} />

      {/* Stats Row */}
      <div className="etf-stats-row">
        <StatCard icon={Layers} label="ETFs Tracked" value={stats.count.toString()} sub={`${formatCurrency(stats.totalAUM)} total AUM`} subType="neutral" color="linear-gradient(135deg, rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.2))" />
        <StatCard icon={ArrowRightLeft} label="Net Flows (30d)" value={formatCurrency(stats.totalFlow)} sub={stats.totalFlow >= 0 ? 'Net inflows' : 'Net outflows'} subType={stats.totalFlow >= 0 ? 'positive' : 'negative'} color="linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(5, 150, 105, 0.2))" />
        <StatCard icon={TrendingUp} label="Avg YTD Return" value={`${stats.avgReturn > 0 ? '+' : ''}${stats.avgReturn}%`} sub="Across all tracked ETFs" subType={stats.avgReturn >= 0 ? 'positive' : 'negative'} color="linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(217, 119, 6, 0.2))" />
        <StatCard icon={Percent} label="Avg Expense Ratio" value={`${stats.avgExpense}%`} sub="Weighted average" subType="neutral" color="linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(109, 40, 217, 0.2))" />
      </div>

      {/* Content Grid */}
      <div className="etf-content-grid">
        {/* Main Table */}
        <div className="etf-table-panel">
          <div className="etf-panel-header">
            <h2><Layers size={16} /> ETF Universe <span className="etf-record-count">{filteredETFs.length}</span></h2>
          </div>

          {/* Toolbar */}
          <div className="etf-toolbar">
            <div className="etf-search-box">
              <Search size={14} />
              <input type="text" placeholder="Search ETF, issuer, or category..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
            </div>
            <div className="etf-filter-toggles">
              {categories.map(f => (
                <button key={f} className={`etf-filter-btn ${categoryFilter === f ? 'active' : ''}`} onClick={() => { setCategoryFilter(f); setCurrentPage(1); }}>{f}</button>
              ))}
            </div>
          </div>

          {/* Table */}
          {filteredETFs.length === 0 ? (
            <div className="etf-empty-state"><Search size={40} /><p>No ETFs match your filters</p></div>
          ) : (
            <div className="etf-table-wrap">
              <table className="etf-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('ticker')}>ETF <SortIcon field="ticker" /></th>
                    <th>Category</th>
                    <th onClick={() => handleSort('aum')}>AUM <SortIcon field="aum" /></th>
                    <th>Price</th>
                    <th>Top Holding</th>
                    <th onClick={() => handleSort('expenseRatio')}>Expense <SortIcon field="expenseRatio" /></th>
                    <th onClick={() => handleSort('ytdReturn')}>YTD <SortIcon field="ytdReturn" /></th>
                    <th onClick={() => handleSort('flow30d')}>Flow (30d) <SortIcon field="flow30d" /></th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedETFs.map(etf => (
                    <tr key={etf.id} onClick={() => navigate(`/stock/${etf.ticker}`)}>
                      <td>
                        <span className="etf-ticker">{etf.ticker}</span>
                        <span className="etf-name">{etf.name}</span>
                      </td>
                      <td><span className="etf-category-badge">{etf.category}</span></td>
                      <td><span className="etf-aum">{formatCurrency(etf.aum)}</span></td>
                      <td><span className="etf-price">${etf.price.toFixed(2)}</span></td>
                      <td>
                        <span className="etf-top-holding">{etf.topHolding}</span>
                        <span className="etf-top-weight">{etf.topWeight}%</span>
                      </td>
                      <td><span className="etf-expense">{etf.expenseRatio.toFixed(2)}%</span></td>
                      <td>
                        <span className={`etf-return ${etf.ytdReturn >= 0 ? 'positive' : 'negative'}`}>
                          {etf.ytdReturn >= 0 ? '+' : ''}{etf.ytdReturn}%
                        </span>
                      </td>
                      <td>
                        <span className={`etf-flow ${etf.flow30d >= 0 ? 'positive' : 'negative'}`}>
                          {etf.flow30d >= 0 ? '+' : ''}{formatCurrency(etf.flow30d)}
                        </span>
                      </td>
                      <td>
                        <button className="etf-chart-btn" onClick={(e) => { e.stopPropagation(); navigate(`/chart/${etf.ticker}`); }} title={`Full chart for ${etf.ticker}`}>
                          <BarChart3 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="etf-pagination">
              <span className="etf-pagination-info">Showing {pagedETFs.length} of {filteredETFs.length} ETFs</span>
              <div className="etf-pagination-btns">
                <button className="etf-page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}><ChevronLeft size={14} /></button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button key={i + 1} className={`etf-page-btn ${currentPage === i + 1 ? 'active' : ''}`} onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                ))}
                <button className="etf-page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}><ChevronRight size={14} /></button>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="etf-sidebar">
          {/* Top Inflows */}
          <div className="etf-sidebar-card">
            <div className="etf-card-header">
              <h3><TrendingUp size={16} color="#10b981" /> Top Inflows (30d)</h3>
            </div>
            <div className="etf-flow-list">
              {mockTopInflows.map((f, i) => (
                <div className="etf-flow-item" key={i} onClick={() => navigate(`/stock/${f.ticker}`)}>
                  <span className={`etf-flow-rank ${i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : ''}`}>{i + 1}</span>
                  <div className="etf-flow-info">
                    <span className="etf-flow-ticker">{f.ticker}</span>
                    <span className="etf-flow-name">{f.name}</span>
                  </div>
                  <div className="etf-flow-data">
                    <span className="etf-flow-amount positive">+{formatCurrency(f.flow)}</span>
                    <span className="etf-flow-pct">{f.pctAUM}% AUM</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Outflows */}
          <div className="etf-sidebar-card">
            <div className="etf-card-header">
              <h3><TrendingDown size={16} color="#ef4444" /> Top Outflows (30d)</h3>
            </div>
            <div className="etf-flow-list">
              {mockTopOutflows.map((f, i) => (
                <div className="etf-flow-item" key={i} onClick={() => navigate(`/stock/${f.ticker}`)}>
                  <span className="etf-flow-rank">{i + 1}</span>
                  <div className="etf-flow-info">
                    <span className="etf-flow-ticker">{f.ticker}</span>
                    <span className="etf-flow-name">{f.name}</span>
                  </div>
                  <div className="etf-flow-data">
                    <span className="etf-flow-amount negative">{formatCurrency(f.flow)}</span>
                    <span className="etf-flow-pct">{f.pctAUM}% AUM</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Category Performance */}
          <div className="etf-sidebar-card">
            <div className="etf-card-header">
              <h3><PieChart size={16} color="#667eea" /> Category YTD Performance</h3>
            </div>
            <div className="etf-category-list">
              {mockCategoryPerformance.map((c, i) => (
                <div className="etf-category-row" key={i}>
                  <span className="etf-cat-name">{c.name}</span>
                  <div className="etf-cat-bar-wrap">
                    <div className="etf-cat-bar-fill" style={{ width: `${Math.min(Math.abs(c.ytd) / 20 * 100, 100)}%`, background: c.ytd >= 0 ? '#10b981' : '#ef4444' }} />
                  </div>
                  <span className={`etf-cat-return ${c.ytd >= 0 ? 'positive' : 'negative'}`}>
                    {c.ytd >= 0 ? '+' : ''}{c.ytd}%
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Largest ETFs */}
          <div className="etf-sidebar-card">
            <div className="etf-card-header">
              <h3><BarChart3 size={16} color="#667eea" /> Largest by AUM</h3>
            </div>
            <div className="etf-flow-list">
              {mockLargestETFs.map((e, i) => (
                <div className="etf-flow-item" key={i} onClick={() => navigate(`/stock/${e.ticker}`)}>
                  <span className={`etf-flow-rank ${i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : ''}`}>{i + 1}</span>
                  <div className="etf-flow-info">
                    <span className="etf-flow-ticker">{e.ticker}</span>
                    <span className="etf-flow-name">{e.name}</span>
                  </div>
                  <div className="etf-flow-data">
                    <span className="etf-flow-amount" style={{ color: '#e2e8f0' }}>{formatCurrency(e.aum)}</span>
                    <span className="etf-flow-pct">{e.expense}% ER</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Hot / Trending ETFs */}
          <div className="etf-sidebar-card">
            <div className="etf-card-header">
              <h3><Zap size={16} color="#f59e0b" /> Trending ETFs</h3>
            </div>
            <div className="etf-flow-list">
              {mockHotETFs.map((e, i) => (
                <div className="etf-flow-item" key={i} onClick={() => navigate(`/stock/${e.ticker}`)}>
                  <div className="etf-hot-icon"><Flame size={14} color="#f59e0b" /></div>
                  <div className="etf-flow-info">
                    <span className="etf-flow-ticker">{e.ticker}</span>
                    <span className="etf-flow-name">{e.name}</span>
                  </div>
                  <div className="etf-flow-data">
                    <span className="etf-flow-amount" style={{ color: '#a78bfa' }}>{e.growth}</span>
                    <span className="etf-flow-pct">{formatCurrency(e.aum)} AUM</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}